import { Component, inject, signal, computed, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ApiService } from '../../core/services/api';
import { EventCardComponent } from '../../shared/components/event-card/event-card';
import { FormsModule } from '@angular/forms';
import { DatePickerComponent } from '../../shared/components/date-picker/date-picker';
import { UiSelectComponent, SelectOption } from '../../shared/components/ui-select/ui-select';
import { CustomTourModalComponent } from '../../shared/components/custom-tour-modal/custom-tour-modal';
import { EventBookingModalComponent } from '../../shared/components/event-booking-modal/event-booking-modal';

import { SeoService } from '../../core/services/seo';

import { NotificationService } from '../../core/services/notification'; // Fix import

@Component({
  selector: 'app-events',
  standalone: true,
  imports: [CommonModule, TranslateModule, EventCardComponent, FormsModule, DatePickerComponent, UiSelectComponent, CustomTourModalComponent, EventBookingModalComponent],
  template: `
    <div class="bg-gray-50 min-h-screen py-12">
      <div class="container mx-auto px-4">
        <!-- Header -->
        <div class="text-center mb-12">
          <h1 class="text-4xl font-bold mb-4 uppercase text-enduro-black">{{ 'NAV.EVENTS' | translate }}</h1>
          <div class="w-24 h-1 bg-enduro-orange mx-auto"></div>
        </div>

        <!-- Filters -->
        <div class="bg-white p-6 rounded-lg shadow-sm mb-8 flex flex-wrap gap-6 items-end">
            <!-- Date Picker -->
            <div class="w-full md:w-56">
                <label class="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                    {{ 'EVENTS.FILTER_DATE' | translate }}
                </label>
                <app-date-picker 
                    [(value)]="dateFilter" 
                    (valueChange)="filterEvents()"
                    [placeholder]="'EVENTS.DATE_PLACEHOLDER' | translate">
                </app-date-picker>
            </div>

            <!-- Difficulty Select -->
            <div class="w-full md:w-56">
                <label class="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                    {{ 'EVENTS.FILTER_DIFFICULTY' | translate }}
                </label>
                <app-ui-select 
                    [(value)]="difficultyFilter"
                    (valueChange)="filterEvents()"
                    [options]="difficultyOptions"
                    [placeholder]="'EVENTS.ALL' | translate"
                    [icon]="difficultyIcon">
                </app-ui-select>
            </div>

            <!-- Toggle Available Spots -->
            <div class="w-full md:w-auto flex items-center mb-2 md:mb-3">
                <label class="inline-flex items-center cursor-pointer group">
                  <input type="checkbox" [(ngModel)]="availableOnly" (ngModelChange)="filterEvents()" class="sr-only peer">
                  <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer 
                              peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full 
                              peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] 
                              after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 
                              after:transition-all peer-checked:bg-enduro-orange transition-colors duration-300"></div>
                  <span class="ms-3 text-sm font-medium text-gray-700 group-hover:text-enduro-orange transition-colors">
                    {{ 'EVENTS.FILTER_AVAILABLE_ONLY' | translate }}
                  </span>
                </label>
            </div>

            <!-- Clear Filters -->
            <div class="w-full md:w-auto mb-2 md:mb-3 md:ml-auto">
                <button 
                    (click)="clearFilters()"
                    [disabled]="!dateFilter && !difficultyFilter && !availableOnly"
                    class="text-sm font-bold text-gray-500 hover:text-enduro-orange disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center uppercase tracking-wide h-6">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    {{ 'EVENTS.CLEAR_FILTERS' | translate }}
                </button>
            </div>
        </div>

        <!-- Grid -->
        @if (loading()) {
            <div class="text-center py-12">
                <p class="text-gray-500">Caricamento eventi...</p>
            </div>
        } @else {
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            @for (event of filteredEvents(); track event.id) {
                <app-event-card [event]="event" (book)="openBookingModal($event)"></app-event-card>
            } @empty {
                <div class="col-span-full text-center py-12">
                    <p class="text-gray-500 text-lg">
                        {{ (dateFilter ? 'EVENTS.NO_EVENTS_DATE' : 'EVENTS.NO_EVENTS_FOUND') | translate }}
                    </p>
                </div>
            }

            </div>

            <!-- Custom Tour Request Button -->
            <div class="mt-12 text-center">
                <button 
                    (click)="showCustomTourModal.set(true)"
                    class="bg-enduro-black text-white hover:bg-enduro-orange transition-colors px-8 py-4 rounded-lg font-bold uppercase tracking-wider shadow-lg flex items-center mx-auto group">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-3 group-hover:animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    {{ 'EVENTS.CUSTOM_TOUR.BUTTON' | translate }}
                </button>
            </div>
        }
      </div>

      <!-- Custom Tour Modal -->
      @if (showCustomTourModal()) {
        <app-custom-tour-modal-v2 (closed)="showCustomTourModal.set(false)"></app-custom-tour-modal-v2>
      }

      <!-- Booking Modal -->
      @if (selectedEventForBooking()) {
          <app-event-booking-modal 
              [event]="selectedEventForBooking()" 
              (closed)="selectedEventForBooking.set(null)"
              (booked)="onBooked()">
          </app-event-booking-modal>
      }
    </div>
  `
})
export class EventsComponent implements OnInit {
  private api = inject(ApiService);
  private notification = inject(NotificationService); // Fix injection
  private seo = inject(SeoService);
  private platformId = inject(PLATFORM_ID);

  events = signal<any[]>([]); // Initialize locally
  filteredEvents = signal<any[]>([]);
  loading = signal<boolean>(true);

  // Restore Filters
  difficultyFilter = '';
  dateFilter = '';
  availableOnly = false;

  // Filters Options
  years = signal<SelectOption[]>([]);
  months = signal<SelectOption[]>([]);
  selectedYear = signal<string>('');
  selectedMonth = signal<string>('');

  showCustomTourModal = signal(false);

  // Computed (moved logic here if complex filtering)

  // Constructor removed as duplicated logic was merged


  ngOnInit() {
    this.seo.setJsonLd({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Calendario Eventi Tour Enduro",
      "description": "Prossimi eventi e tour enduro in programma.",
      "url": "https://enduromarghine.it/events"
    });

    // Only fetch on browser to avoid build-time errors if API is not available
    if (isPlatformBrowser(this.platformId)) {
      this.fetchEvents();
    } else {
      // Optional: Set some static initial state for SSR if desired, or leave empty
      this.loading.set(false);
    }
  }

  fetchEvents() {
    this.loading.set(true);
    this.api.getEvents().subscribe({
      next: (data) => {
        this.events.set(data);
        this.filteredEvents.set(data);
        this.filterEvents(); // Apply filters initially
        this.updateJsonLd(data);
        this.loading.set(false);
      },
      error: (e) => {
        console.error('Error loading events', e);
        this.loading.set(false);
      }
    });
  }

  updateJsonLd(events: any[]) {
    const schemaEvents = events.map(event => ({
      "@type": "Event",
      "name": event.title_it,
      "description": event.description_it,
      "startDate": event.event_date.replace(' ', 'T'), // Ensure ISO format
      "endDate": event.event_date.replace(' ', 'T'), // Same day duration default
      "eventStatus": "https://schema.org/EventScheduled",
      "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
      "location": {
        "@type": "Place",
        "name": event.location || "Sardegna",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Via Manigos 5",
          "addressLocality": "Bolotana",
          "postalCode": "08011",
          "addressRegion": "NU",
          "addressCountry": "IT"
        }
      },
      "image": event.image_url ? [event.image_url] : ['https://tourenduromarghineexperience.com/assets/logo.png'],
      "offers": {
        "@type": "Offer",
        "url": "https://tourenduromarghineexperience.com/events",
        "priceCurrency": "EUR",
        "availability": (event.current_participants < event.max_participants) ? "https://schema.org/InStock" : "https://schema.org/SoldOut",
        "validFrom": new Date().toISOString()
      },
      "organizer": {
        "@type": "Organization",
        "name": "Tour Enduro Marghine Experience",
        "url": "https://tourenduromarghineexperience.com"
      }
    }));

    this.seo.setJsonLd({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Calendario Eventi Tour Enduro",
      "description": "Prossimi eventi e tour enduro in programma.",
      "url": "https://tourenduromarghineexperience.com/events",
      "mainEntity": schemaEvents
    });
  }
  selectedEventForBooking = signal<any>(null);

  difficultyOptions: SelectOption[] = [
    { label: 'EVENTS.ALL', value: '' },
    { label: 'EVENTS.DIFFICULTY_EASY', value: 'easy' },
    { label: 'EVENTS.DIFFICULTY_MEDIUM', value: 'medium' },
    { label: 'EVENTS.DIFFICULTY_HARD', value: 'hard' }
  ];

  difficultyIcon = `
    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  `;

  constructor() {
    this.loadEvents();
  }

  openBookingModal(event: any) {
    this.selectedEventForBooking.set(event);
  }

  onBooked() {
    this.loadEvents(); // Refresh list to update participant counts
  }

  loadEvents() {
    this.loading.set(true);
    this.api.get<any[]>('events').subscribe({
      next: (data) => {
        this.events.set(data);
        this.filterEvents();
        this.loading.set(false);
      },
      error: (err) => {
        console.error('API Error:', err);
        this.loading.set(false);
      }
    });
  }

  filterEvents() {
    let filtered = this.events();

    // Difficulty Filter
    if (this.difficultyFilter) {
      filtered = filtered.filter(e => e.difficulty === this.difficultyFilter);
    }

    // Date Filter (Exact date)
    if (this.dateFilter) {
      filtered = filtered.filter(e => {
        if (!e.event_date) return false;
        // Simple string comparison YYYY-MM-DD
        // event_date format is expected to be 'YYYY-MM-DD HH:MM:SS'
        const eventDate = e.event_date.split(' ')[0];
        return eventDate === this.dateFilter;
      });
    }

    // Available Spots Filter
    if (this.availableOnly) {
      filtered = filtered.filter(e => {
        const max = Number(e.max_participants) || 10;
        const current = Number(e.current_participants) || 0;
        return current < max;
      });
    }

    this.filteredEvents.set(filtered);
  }

  clearFilters() {
    this.dateFilter = '';
    this.difficultyFilter = '';
    this.availableOnly = false;
    this.filterEvents();
  }

  toggleAvailability() {
    this.availableOnly = !this.availableOnly;
    this.filterEvents();
  }
}
